import { getClientId } from "@smartsheet/lodestar-utils";
import React, { forwardRef, HTMLAttributes } from "react";
import { useI18n } from "../../../../i18n/i18n";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { VisuallyHidden } from "../../../visuallyhidden";
import { ActionButton } from "../shared/ActionButton";
import { ToggleButtonIcon } from "./ToggleButtonIcon";

export interface ToggleButtonProps extends HTMLAttributes<HTMLButtonElement> {
    clientId: string | undefined;
    hideDropdownArrow: boolean;
    isReadonly: boolean;
    isActive?: boolean;
    isHovered?: boolean;
    isOpen?: boolean;
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
    ({ clientId, hideDropdownArrow, isReadonly, isHovered, isActive, isOpen, ...rest }, ref) => {
        const { i18n } = useI18n();
        const themeMode = useCombinedThemeMode();

        const sharedProps = {
            ...rest,
            "aria-label": i18n._(
                /*i18n*/ {
                    comment: "This button opens and closes the select menu",
                    id: "Toggle menu",
                }
            ),
            ref,
        };

        // If the button is readonly, we need to to still be there so we can interact with it.
        // However, we don't want it to be visible at all--same with hideDropdownArrow, by no coincidence.
        return isReadonly || hideDropdownArrow ? (
            <VisuallyHidden renderAs="button" {...sharedProps} aria-disabled="true">
                {null}
            </VisuallyHidden>
        ) : (
            <ActionButton
                {...sharedProps}
                // Pass isDisabled here for Clickable to update its state.
                isDisabled={isReadonly}
                omitFocusRing
                isHovered={isHovered}
                isActive={isActive}
                isOpen={isOpen}
                clientId={getClientId(clientId, "-toggle")}
                themeMode={themeMode}
            >
                <ToggleButtonIcon />
            </ActionButton>
        );
    }
);

ToggleButton.displayName = "ToggleButton";
